import { useEffect, useState } from 'react';
import { Anchor, Badge, Card, Code, Group, Stack, Text, Title } from '@mantine/core';
import { Link, useParams } from 'react-router-dom';
import { contractPartyRoleLabel, type ContractDetail } from '@expeditor/shared';
import { contractsApi } from '../../shared/api/quotes';
import { useRolePrefix } from '../../shared/auth/useRolePrefix';

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const prefix = useRolePrefix();
  const [c, setC] = useState<ContractDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    void contractsApi
      .getOne(id)
      .then(setC)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'));
  }, [id]);

  if (error) return <Text c="red">{error}</Text>;
  if (!c) return <Text c="dimmed">Загрузка…</Text>;

  return (
    <Stack>
      <Group>
        <Title order={2}>Договор {c.number}</Title>
        <Badge color={c.endedAt ? 'gray' : 'green'}>
          {c.endedAt ? 'Завершён' : 'Действующий'}
        </Badge>
      </Group>

      <Card withBorder>
        <Title order={4}>Предмет договора</Title>
        <Text mt="xs">{c.subject}</Text>
        <Group mt="xs">
          <Text size="sm" c="dimmed">
            Подписан: {new Date(c.signedAt).toLocaleString('ru-RU')}
          </Text>
          {c.endedAt && (
            <Text size="sm" c="dimmed">
              Завершён: {new Date(c.endedAt).toLocaleString('ru-RU')}
            </Text>
          )}
        </Group>
      </Card>

      <Card withBorder>
        <Title order={4}>Поручение</Title>
        <Anchor component={Link} to={`${prefix}/orders/${c.order.id}`}>
          {c.order.number}
        </Anchor>
        <Text>
          {c.order.cargoName} · {c.order.cargoWeightKg} кг
        </Text>
      </Card>

      {c.quote && (
        <Card withBorder>
          <Title order={4}>Условия (из котировки)</Title>
          <Text fw={500} mt="xs">
            {c.quote.amount.toLocaleString('ru-RU')} {c.quote.currency}
          </Text>
          <Code block mt="sm">
            {c.quote.workScope}
          </Code>
        </Card>
      )}

      <Card withBorder>
        <Title order={4}>Привлекаемые исполнители</Title>
        {c.parties.length === 0 ? (
          <Text c="dimmed" size="sm">
            Перевозчики и субподрядчики ещё не привязаны. Появится на этапе 6
            (Перевозка).
          </Text>
        ) : (
          <Stack mt="sm">
            {c.parties.map((p) => (
              <Group key={p.id}>
                <Badge>{contractPartyRoleLabel(p.role)}</Badge>
                {p.carrier && (
                  <Text>
                    {p.carrier.fullName}
                    {p.carrier.inn && ` (ИНН ${p.carrier.inn})`}
                  </Text>
                )}
              </Group>
            ))}
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
